/**
 * Parça Takip dinamik bileşenleri (paylaşım sistemi kartları).
 * Ana parça kartı (parca-takip) + her alt kart tipi (parca-not/olcu/agirlik/durum/onay/teklif/foto).
 * Hepsi paylaşım feed'inde otomatik render olur. main.ts'e import edilir.
 */
import { DinamikBilesen } from '@service/fikoyt-ui/decorators/dinamik-bilesen';
import { DinamikBilesenRegistry } from '@service/fikoyt-ui/src/lib/dinamik-bilesen/dinamik-bilesen.registry';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GameCardBase, GC_BASE_CSS } from '@/src/app/uygulamalar/hmi-pano/pano/game-card-base';
import { paramDeger } from '@/src/app/shared/utils/param-deger.utils';
import { ASAMA_BILGI, ALT_TIP_BILGI, ParcaAsama, ParcaAltTip } from './interfaces/parca-takip.interface';

/* ───────────────────────── ANA PARÇA KARTI ───────────────────────── */
@Component({
  selector: 'parca-takip-kart',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="gc gc--parca" [style.--accent]="renk()" [style.--accent-rgb]="rgb()" (mousemove)="mouseMove($event)" (mouseleave)="mouseLeave()" [ngStyle]="kartStil">
      <div class="gc-canli-bg"></div><div class="gc-parlak-kenar"></div>
      <div class="gc-kose gc-kose--sol-ust"></div><div class="gc-kose gc-kose--sag-ust"></div><div class="gc-kose gc-kose--sol-alt"></div><div class="gc-kose gc-kose--sag-alt"></div>
      <div class="gc-rarity-serit"></div><div class="gc-ic-glow"></div><div class="gc-shimmer"></div>
      <div class="gc-kenar-glow" [style.background]="kenarGlow"></div>
      <div class="gc-icerik">
        <div class="gc-hud-baslik">📦 PARÇA TAKİP</div>
        <div class="pt-baslik">{{ d('parcaAdi') || 'İsimsiz Parça' }}@if (d('revizyon')) { <span class="pt-rev">{{ d('revizyon') }}</span> }</div>
        @if (gecikiyor()) { <div class="pt-gecikme">⚠️ {{ gecikmeGunu() }} gün gecikmiş</div> }
        @if (d('malzeme')) { <div class="pt-alt">{{ d('malzeme') }}</div> }
        <div class="pt-agirlik">@if (teorik()) { <span>⚖️ {{ teorik() }} kg</span> } @if (olculen()) { <span class="pt-olc">/ ölç. {{ olculen() }} kg</span> }</div>
        <div class="pt-durum" [style.background]="'rgba('+rgb()+',.22)'" [style.color]="renk()" [style.border-color]="'rgba('+rgb()+',.5)'">{{ asamaSimge() }} {{ asama() }}</div>
        <div class="gc-xp-bar"><div class="gc-xp-dolgu" [style.width.%]="gorunur() ? ilerleme() : 0" [style.background]="renk()"></div></div>
      </div>
    </div>
  `,
  styles: [GC_BASE_CSS, `
    .pt-baslik{font-size:14px;font-weight:700;color:var(--py-metin,#e2e8f0);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;display:flex;align-items:center;gap:6px;}
    .pt-rev{font-size:9px;padding:1px 6px;border-radius:3px;background:rgba(var(--accent-rgb),.2);color:var(--accent);font-weight:600;}
    .pt-alt{font-size:11px;color:var(--py-metin-ikincil,#94a3b8);}
    .pt-agirlik{font-size:11px;color:#9fe6ff;font-family:monospace;display:flex;gap:6px;align-items:baseline;}
    .pt-olc{color:#fbbf24;}
    .pt-durum{display:inline-block;padding:3px 12px;border-radius:4px;font-size:10px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;border:1.5px solid;margin-top:4px;}
    .pt-gecikme{display:flex;align-items:center;gap:4px;padding:3px 8px;border-radius:3px;background:rgba(255,77,79,.12);border-left:2px solid #ff4d4f;font-size:9px;font-weight:700;color:#ff4d4f;}
  `],
})
export class ParcaTakipKartComponent extends GameCardBase {
  parametreler = input<any>();
  _hesap = input<any>();
  constructor() { super(); }
  d(ismi: string) { return paramDeger(this.parametreler()?.ek, ismi); }
  asama = computed<ParcaAsama>(() => (this._hesap()?.sonAsama || this.d('asama') || 'Teklif') as ParcaAsama);
  bilgi = computed(() => ASAMA_BILGI[this.asama()] || ASAMA_BILGI['Teklif']);
  renk = computed(() => this.bilgi().renk);
  rgb = computed(() => this.bilgi().rgb);
  ilerleme = computed(() => this.bilgi().ilerleme);
  asamaSimge = computed(() => this.bilgi().simge);
  teorik = computed(() => this.d('teorikAgirlik'));
  olculen = computed(() => this.d('olculenAgirlik'));
  gecikiyor = computed(() => { const h = this.d('hedefTarih'); return h && new Date(h) < new Date() && this.asama() !== 'Teslim'; });
  gecikmeGunu = computed(() => { const h = this.d('hedefTarih'); if (!h || !this.gecikiyor()) return 0; return Math.ceil((Date.now() - new Date(h).getTime()) / 86400000); });
}

/* ───────────────────────── GENERIC ALT KART ───────────────────────── */
@Component({
  selector: 'parca-alt-kart',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="pak" [style.border-left-color]="bilgi().renk">
      <div class="pak-bas"><span class="pak-sim">{{ bilgi().simge }}</span><span class="pak-tip">{{ bilgi().baslik }}</span></div>
      <div class="pak-govde">{{ ozet() }}</div>
    </div>
  `,
  styles: [`
    .pak{background:var(--py-arkaplan-yukselti,#0c0e14);border:1px solid rgba(255,255,255,.06);border-left:3px solid #38bdf8;border-radius:5px;padding:7px 10px;display:flex;flex-direction:column;gap:3px;}
    .pak-bas{display:flex;align-items:center;gap:6px;font-size:10px;font-weight:700;color:var(--py-metin-ikincil,#94a3b8);text-transform:uppercase;letter-spacing:.05em;}
    .pak-sim{font-size:13px;}
    .pak-govde{font-size:12px;color:var(--py-metin,#e2e8f0);line-height:1.4;white-space:pre-wrap;}
  `],
})
export class ParcaAltKartComponent {
  parametreler = input<any>();
  d(ismi: string) { return paramDeger(this.parametreler()?.ek, ismi); }
  tip = computed<ParcaAltTip>(() => (this.d('altTip') || 'NOT') as ParcaAltTip);
  bilgi = computed(() => ALT_TIP_BILGI[this.tip()] || ALT_TIP_BILGI.NOT);
  ozet = computed(() => {
    switch (this.tip()) {
      case 'NOT': return this.d('metin') || '—';
      case 'OLCU': return `${this.d('olcuTuru') || 'Ölçü'}: ${this.d('deger') ?? '—'} ${this.d('birim') || 'mm'}`;
      case 'AGIRLIK': return `${this.d('kg') ?? '—'} kg${this.d('yontem') ? ' · ' + this.d('yontem') : ''}`;
      case 'DURUM': return `${this.d('simge') || ''} ${this.d('asama') || '—'}${this.d('aciklama') ? ' — ' + this.d('aciklama') : ''}`;
      case 'ONAY': return `${this.d('sonuc') || 'Onay'}${this.d('onaylayan') ? ' · ' + this.d('onaylayan') : ''}`;
      case 'TEKLIF': return `${this.d('fiyat') ?? '—'} ${this.d('parabirimi') || '₺'}${this.d('sure') ? ' · ' + this.d('sure') + ' dk' : ''}`;
      case 'FOTO': return this.d('aciklama') || '📷 Fotoğraf';
      default: return '—';
    }
  });
}

/* ───────────────────────── KAYIT ───────────────────────── */
@DinamikBilesen({ id: 'parca-takip', baslik: 'Parça Takip', aciklama: 'STP parça üretim & inceleme takibi', ikon: 'container', kategori: 'parca-takip' })
class ParcaTakip {
  paylas_kutusu = ParcaTakipKartComponent;
  paylasilmis_kart = ParcaTakipKartComponent;
  paylasilmis_kart_sade = ParcaTakipKartComponent;
  hesaplamalar = {
    prepipe: [
      { $lookup: { from: 'hareketler', let: { pid: '$_id' }, pipeline: [
        { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, 'icerikler.0.ek.altTip': 'DURUM', silindi: { $ne: true } } },
        { $sort: { _id: -1 } }, { $limit: 1 },
      ], as: '_durumAlt' } },
      { $addFields: { '_hesap.sonAsama': { $ifNull: [{ $arrayElemAt: ['$_durumAlt.icerikler.0.ek.asama', 0] }, '$veri.asama'] } } },
      { $project: { _durumAlt: 0 } },
    ],
  };
}

// Alt kart tipleri — hepsi generic ParcaAltKartComponent ile render (registry'ye doğrudan kaydet).
for (const tip of Object.keys(ALT_TIP_BILGI) as ParcaAltTip[]) {
  const bilgi = ALT_TIP_BILGI[tip];
  DinamikBilesenRegistry.kaydet(
    bilgi.id,
    { bilesen_id: bilgi.id, meta: { id: bilgi.id, baslik: bilgi.baslik, kategori: 'parca-takip' }, paylas_kutusu: ParcaAltKartComponent, paylasilmis_kart: ParcaAltKartComponent } as any,
    { id: bilgi.id, baslik: bilgi.baslik, kategori: 'parca-takip' },
  );
}
